import { Injectable } from '@angular/core';
import { fromEvent, map, Subject, takeUntil } from 'rxjs';
import * as cornerstone from 'cornerstone-core';

import { StateManagerProjectService } from '@state-manager';
import { CornerstoneTool, ViewWindowIndex } from '@viewer/data';
import { IReferenceLineItem } from '@viewer/cornerstone.data';

@Injectable()
export class ReferenceLineService {
  private _unsubscribe$ = new Subject();

  private _needCallRedrawInEnd: IReferenceLineItem | null = null;

  constructor(
    private _stateManagerProjectService: StateManagerProjectService
  ) {}

  public init(viewerElements: { [key: string]: HTMLElement }): void {
    // this subscription is needed to draw reference lines
    // while switching between viewers (without scrolling)
    this._stateManagerProjectService.currentSelectedWindow$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentSelectedWindow) => {
        if (
          !this._stateManagerProjectService.isViewerLoaded ||
          currentSelectedWindow === ViewWindowIndex.Empty
        ) {
          return;
        }

        Array.from(Array(3)).forEach((_, index) => {
          cornerstone.updateImage(viewerElements[index + 1]);
        });
      });
  }

  public destroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
  }

  public subscribeForDrawReferenceLine(
    element: HTMLElement,
    viewWindowIndex: ViewWindowIndex,
    viewerElements: { [key: string]: HTMLElement }
  ): void {
    fromEvent(element, 'cornerstoneimagerendered')
      .pipe(
        map((event) => event as CustomEvent),
        takeUntil(this._unsubscribe$)
      )
      .subscribe((event) => {
        this._setCallToRedraw(event, viewWindowIndex, viewerElements);
        this._drawReferenceLine(
          viewWindowIndex,
          event.detail.canvasContext,
          event.detail.image,
          viewerElements
        );
      });

    this._stateManagerProjectService.mouseWasDragged$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((event: boolean) => {
        if (
          this._stateManagerProjectService.currentTool ===
            CornerstoneTool.Wwwc &&
          !event &&
          this._needCallRedrawInEnd !== null
        ) {
          this._drawReferenceLine(
            this._needCallRedrawInEnd.viewWindowIndex,
            this._needCallRedrawInEnd.canvasContext,
            this._needCallRedrawInEnd.image,
            this._needCallRedrawInEnd.viewerElements
          );
          this._needCallRedrawInEnd = null;
        }
      });
  }

  private _setCallToRedraw(
    event: CustomEvent,
    viewWindowIndex: ViewWindowIndex,
    viewerElements: { [key: string]: HTMLElement }
  ) {
    if (
      this._stateManagerProjectService.currentSelectedWindow ===
        ViewWindowIndex.Model3D ||
      (this._stateManagerProjectService.currentTool === CornerstoneTool.Wwwc &&
        this._stateManagerProjectService.mouseWasDragged)
    ) {
      this._needCallRedrawInEnd =
        this._stateManagerProjectService.currentTool === CornerstoneTool.Wwwc &&
        this._stateManagerProjectService.currentSelectedWindow ===
          viewWindowIndex
          ? {
              viewWindowIndex: viewWindowIndex,
              canvasContext: event.detail.canvasContext,
              image: event.detail.image,
              viewerElements: viewerElements,
            }
          : this._needCallRedrawInEnd
          ? this._needCallRedrawInEnd
          : null;
      return;
    }
  }

  private _drawReferenceLine(
    index: ViewWindowIndex,
    context: CanvasRenderingContext2D,
    image: { height: number; width: number },
    viewerElements: { [key: string]: HTMLElement }
  ): void {
    const currentSelectedWindowIndex =
      this._stateManagerProjectService.currentSelectedWindow;

    if (
      !this._stateManagerProjectService.isViewerLoaded ||
      !currentSelectedWindowIndex
    ) {
      return;
    }

    if (currentSelectedWindowIndex === index) {
      [ViewWindowIndex.Coronal, ViewWindowIndex.Axial, ViewWindowIndex.Sagittal]
        .filter((windowIndex) => windowIndex !== index)
        .forEach((windowIndex) =>
          cornerstone.updateImage(viewerElements[windowIndex])
        );

      return;
    }

    const imageHeight = image.height;
    const imageWidth = image.width;
    context.save();
    context.strokeStyle = 'orange';
    const viewerId = `viewer${currentSelectedWindowIndex}`;
    let lineTopOffset =
      (this._stateManagerProjectService.currentActiveSliceIndex[viewerId] /
        this._stateManagerProjectService.totalNumberOfSlices[viewerId]) *
      imageHeight;

    let lineLeftOffset =
      (this._stateManagerProjectService.currentActiveSliceIndex[viewerId] /
        this._stateManagerProjectService.totalNumberOfSlices[viewerId]) *
      imageWidth;

    context.beginPath();

    switch (currentSelectedWindowIndex) {
      case ViewWindowIndex.Coronal:
        if (index === ViewWindowIndex.Axial) {
          context.moveTo(0, lineTopOffset);
          context.lineTo(imageWidth, lineTopOffset);
        } else if (index === ViewWindowIndex.Sagittal) {
          context.moveTo(lineLeftOffset, 0);
          context.lineTo(lineLeftOffset, imageHeight);
        }
        break;

      case ViewWindowIndex.Axial:
        lineTopOffset = imageHeight - lineTopOffset;
        context.moveTo(0, lineTopOffset);
        context.lineTo(imageWidth, lineTopOffset);
        break;

      case ViewWindowIndex.Sagittal:
        lineLeftOffset = imageWidth - lineLeftOffset;
        context.moveTo(lineLeftOffset, 0);
        context.lineTo(lineLeftOffset, imageHeight);
        break;
    }

    context.stroke();
    context.restore();
  }
}
